import React, { useCallback, useEffect, useState } from "react";
import { isTaskMantisDesktop } from "../desktopUpdate";
import SettingsToggleRow from "./shared/SettingsToggleRow";

const DEFAULT_PREFERENCES = { capturing: true, captured: true };

/**
 * Settings section for the macOS notifications the capture hotkey shows.
 * Preferences live in the Electron main process (the hotkey fires with no
 * window open), so this reads and writes them over IPC rather than
 * localStorage.
 *
 * @example
 * <DesktopNotificationSettingsSection />
 * // Renders "Notifications" in Electron; nothing in the browser
 */
const DesktopNotificationSettingsSection = () => {
  const [preferences, setPreferences] = useState(DEFAULT_PREFERENCES);

  useEffect(() => {
    if (!isTaskMantisDesktop()) return undefined;
    let cancelled = false;
    window.starmeshDesktop
      .getNotificationPreferences?.()
      .then((next) => {
        if (cancelled || !next) return;
        setPreferences({
          capturing: next.capturing !== false,
          captured: next.captured !== false,
        });
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const updatePreference = useCallback((key, enabled) => {
    // Optimistic: the toggle only gates future notifications, so a failed
    // write costs nothing worse than a stale switch until the next open.
    setPreferences((prev) => ({ ...prev, [key]: enabled }));
    void window.starmeshDesktop?.setNotificationPreferences?.({ [key]: enabled })
      ?.then?.((stored) => {
        if (!stored) return;
        setPreferences({
          capturing: stored.capturing !== false,
          captured: stored.captured !== false,
        });
      })
      ?.catch?.(() => {});
  }, []);

  if (!isTaskMantisDesktop()) return null;

  return (
    <section className="settings-general-section">
      <h3 className="settings-general-section__title">Notifications</h3>
      <p className="settings-general-section__desc">
        Notifications the desktop app shows when you capture with Ctrl+Option+K.
        Capture failures always notify.
      </p>
      <div className="settings-general-block settings-general-block--nested">
        <SettingsToggleRow
          label="Capturing selection"
          description="Confirms the hotkey registered. Disappears on its own once the task is created."
          pressed={preferences.capturing}
          onChange={(next) => updatePreference("capturing", next)}
        />
        <SettingsToggleRow
          label="Task captured"
          description="Shows the new task's title when it's saved. Click it to open the task."
          pressed={preferences.captured}
          onChange={(next) => updatePreference("captured", next)}
        />
      </div>
    </section>
  );
};

export default DesktopNotificationSettingsSection;
